import React, { memo } from "react";
import { Text, View, ScrollView } from "react-native";
import Svg, { Circle } from "react-native-svg";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { getCategoryConfig, formatCurrencyWhole } from "@/utils/myMoney";
import { UserFinanceDataResponse, CategoryBudget } from "@/types/financeData";
import useWindow from "@/hooks/useWindow";

interface SpendingCategoriesCardProps {
  financeData: UserFinanceDataResponse | null;
}

const RING_SIZE = 48;
const STROKE_WIDTH = 4;
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Circular progress ring with emoji in the center
const CategoryProgressRing = ({
  progress,
  color,
  emoji,
}: {
  progress: number;
  color: string;
  emoji: string;
}) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 1);
  const strokeDashoffset = CIRCUMFERENCE * (1 - clampedProgress);

  return (
    <View style={styles.ringContainer}>
      <Svg width={RING_SIZE} height={RING_SIZE}>
        {/* Background track */}
        <Circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          stroke="#E5E7EB"
          strokeWidth={STROKE_WIDTH}
          fill="none"
        />
        {/* Progress arc */}
        <Circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          stroke={color}
          strokeWidth={STROKE_WIDTH}
          fill="none"
          strokeDasharray={`${CIRCUMFERENCE}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          rotation="-90"
          origin={`${RING_SIZE / 2}, ${RING_SIZE / 2}`}
        />
      </Svg>
      <Text style={styles.ringEmoji}>{emoji}</Text>
    </View>
  );
};

// Category card item
const CategoryItem = ({ budget }: { budget: CategoryBudget }) => {
  const spent = budget.spentAmount;
  const total = budget.budgetAmount;
  const left = Math.max(0, total - spent);
  const categoryConfig = getCategoryConfig([budget.category]);
  const progress = total > 0 ? spent / total : 0;

  return (
    <View style={styles.categoryCard}>
      <Text style={styles.categoryName}>{categoryConfig.name}</Text>
      <View style={styles.amountSection}>
        <Text style={styles.spentAmount}>{formatCurrencyWhole(spent)}</Text>
        <Text style={styles.spentLabel}>spent</Text>
      </View>
      <View style={styles.leftSection}>
        <Text style={styles.leftAmount}>{formatCurrencyWhole(left)}</Text>
        <Text style={styles.leftLabel}>left</Text>
        <CategoryProgressRing
          progress={progress}
          color={spent > total ? "#EF4444" : "#0F9129"}
          emoji={categoryConfig.emoji}
        />
      </View>
    </View>
  );
};

export default memo(function SpendingCategoriesCard({
  financeData,
}: SpendingCategoriesCardProps) {
  const budgets = financeData?.analysis?.budgets || [];
  const { isDesktop } = useWindow();
  if (!budgets.length) {
    return null;
  }

  if (isDesktop) {
    return (
      <View
        style={{
          flexWrap: "wrap",
          flexDirection: "row",
          gap: 16,
          paddingLeft: 16,
        }}
      >
        {budgets.map((budget, index) => (
          <CategoryItem key={budget.category + index} budget={budget} />
        ))}
      </View>
    );
  }

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
    >
      {budgets.map((budget, index) => (
        <CategoryItem key={budget.category + index} budget={budget} />
      ))}
    </ScrollView>
  );
});

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 16,
    gap: 12,
    paddingVertical: 16,
  },
  categoryCard: {
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    width: 160,
    flexGrow: 1,
    ...Shadows.card,
  },
  categoryName: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#A3A3A3",
    marginBottom: 8,
  },
  amountSection: {
    marginBottom: 8,
  },
  spentAmount: {
    fontSize: 24,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 28,
  },
  spentLabel: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    lineHeight: 16,
  },
  leftSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  leftAmount: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 20,
  },
  leftLabel: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    lineHeight: 16,
    flex: 1,
  },
  ringContainer: {
    width: RING_SIZE,
    height: RING_SIZE,
    alignItems: "center",
    justifyContent: "center",
  },
  ringEmoji: {
    fontSize: 22,
    position: "absolute",
  },
});
